// Add new blog posts here. Each post is rendered on /blogs (grid) and /blogs/:slug (detail).
// To add a post: copy an entry, change slug/title/date/hero/excerpt/content, save. Done.
//
// content[] supports these block types:
//   { type: "p", text: "..." }
//   { type: "h2", text: "..." }
//   { type: "ul", items: ["...", "..."] }
//   { type: "image", src: "...", alt: "..." }  → renders clickable, opens lightbox
//
// Inline links in text: use raw HTML, e.g. text: 'Visit <a href="...">our site</a>'

export const BLOG_POSTS = [
    {
        slug: "haitian-griot-fried-pork-boston-meal-prep",
        title:
            "Haitian Griot: The Crispy Fried Pork at the Heart of Every Haitian Celebration",
        excerpt:
            "Twice-cooked Haitian fried pork, served with black rice and fresh vegetables — meal-prepped weekly for Greater Boston. The dish at every birthday, every holiday, every gathering that matters.",
        hero_image:
            "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/7y6l6i5u_Screenshot_20260514_140836_ChatGPT.jpg",
        author: "Caribbean Meal Prep Team",
        date: "June 9, 2026",
        read_time: "5 min read",
        category: "Haitian Heritage",
        keywords: [
            "Haitian griot",
            "Haitian fried pork",
            "griot meal prep Boston",
            "Haitian food delivery Massachusetts",
            "authentic Haitian meals",
        ],
        content: [
            {
                type: "p",
                text: "<em>Twice-cooked Haitian fried pork, served with black rice and fresh vegetables — meal-prepped weekly for Greater Boston.</em>",
            },
            {
                type: "p",
                text: "Ask any Haitian to name the one dish that means celebration, and you'll hear the same answer every time: <strong>griot</strong>. It's the sound of pork crackling in the pan on a Saturday afternoon. It's the dish that shows up at every birthday, every holiday, every gathering that matters. If Haitian food had a flag, griot would be on it.",
            },
            {
                type: "p",
                text: "At <strong>Caribbean Meal Prep</strong>, griot is one of our most-loved meals — hand-made every week and delivered fresh across Greater Boston. Here's what makes it so special, and why it deserves a spot in your weekly rotation.",
            },
            { type: "h2", text: "What Is Haitian Griot?" },
            {
                type: "p",
                text: "Haitian griot (also spelled <em>griyo</em>) is Haiti's iconic fried pork dish. It starts with chunks of pork shoulder that are marinated, slow-braised until they're fall-apart tender, then fried until the edges turn deep golden and crisp.",
            },
            {
                type: "p",
                text: "That two-step method is the whole secret. The braising keeps the inside juicy and soft. The frying gives you that signature crackle on the outside. One bite and you get both — crispy and tender in the same mouthful.",
            },
            { type: "h2", text: "The Secret Is in the Marinade" },
            {
                type: "p",
                text: "Great griot is built long before it hits the pan. The pork is marinated in <strong>epis</strong> — the Haitian seasoning base made from peppers, garlic, onions, and fresh herbs — along with bright citrus and a kick of Scotch bonnet pepper.",
            },
            {
                type: "p",
                text: "The longer it marinates, the deeper the flavor goes. This is why authentic griot can't be rushed. It's a dish that rewards patience, and you can taste every hour of it.",
            },
            {
                type: "image",
                src: "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/h8ftotd6_Screenshot_20260514_140852_ChatGPT.jpg",
                alt: "Crispy Haitian griot with fried plantains and pikliz",
                caption:
                    "Crispy on the outside, tender on the inside — golden Haitian griot, the centerpiece of every Sunday plate.",
            },
            { type: "h2", text: "How We Serve Our Griot" },
            {
                type: "p",
                text: "At Caribbean Meal Prep, our griot comes plated the way it's meant to be enjoyed — alongside our <strong>diri djon djon</strong> (Haitian black rice) and a generous serving of fresh mixed vegetables: broccoli, carrots, and peppers.",
            },
            {
                type: "p",
                text: "It's a complete, balanced Haitian meal — rich, satisfying, and ready the moment you are.",
            },
            { type: "h2", text: "Why Griot Belongs in Your Weekly Meal Prep" },
            {
                type: "p",
                text: "Here's the honest truth: griot done right is a labor of love. The marinating, the braising, the frying — it's a weekend project, not a Tuesday-night-after-work meal.",
            },
            {
                type: "p",
                text: "That's exactly why we do it for you. We're a meal prep service, not a restaurant. We cook your griot fresh each week and deliver it across Greater Boston, so you can eat the Haitian food you grew up loving without giving up an evening to make it.",
            },
            { type: "h2", text: "Built for Busy People — and Better Together" },
            {
                type: "p",
                text: "One thing our customers love: the more you order, the more you save. Our weekly meal prep is built to make eating well easy and affordable.",
            },
            {
                type: "ul",
                items: [
                    "Orders start at just <strong>3 meals</strong>",
                    "Order <strong>6 meals</strong> and get <strong>10% off</strong>",
                    "Order <strong>9 meals</strong> and get <strong>15% off</strong>",
                    "Order <strong>12 meals</strong> and get <strong>20% off</strong>",
                    "Become a <strong>subscriber</strong> and get an extra <strong>10% off</strong> every single order",
                ],
            },
            {
                type: "p",
                text: "That makes Caribbean Meal Prep a perfect fit for families, roommates, and even coworkers who want to go in on an order together. Split a 12-meal order with the household and everyone eats like it's Sunday — all week long.",
            },
            { type: "h2", text: "Haitian Griot Delivered Across Greater Boston" },
            {
                type: "p",
                text: "If you've been searching for <strong>Haitian fried pork</strong> or <strong>Haitian food delivery in Massachusetts</strong>, our griot is the place to start. We bring authentic Haitian meal prep to hard-working communities all over the region, including:",
            },
            {
                type: "ul",
                items: [
                    "<strong>Boston</strong> — Mattapan, Hyde Park, and Dorchester",
                    "<strong>Brockton, Randolph, and Milton</strong>",
                    "<strong>Cambridge, Somerville, and Everett</strong>",
                    "<strong>Quincy, Malden, Newton, and Waltham</strong>",
                ],
            },
            {
                type: "p",
                text: "Whether you're working long shifts, commuting into the city, or running a busy household, we make it easy to come home to a real Haitian meal.",
            },
            { type: "h2", text: "Order Your Griot This Week" },
            {
                type: "p",
                text: "Crispy, tender, slow-marinated Haitian griot — served with black rice and fresh vegetables, hand-made weekly and delivered to your door. No hours at the stove, no compromise on flavor.",
            },
            {
                type: "p",
                text: '<strong>Reserve your griot meals this week and taste Haiti without leaving home.</strong> <a href="/menu">Order now →</a>',
            },
        ],
    },
    {
        slug: "diri-djon-djon-haitian-black-rice-boston",
        title:
            "Diri Djon Djon: The Story of Haiti's Legendary Black Rice — Delivered Fresh Across Greater Boston",
        excerpt:
            "Authentic Haitian black rice, hand-made weekly for busy professionals across Greater Boston. The story behind the dish — and why getting it right takes real time.",
        hero_image:
            "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/j7hz0dha_Diri%20Ak%20DjonDjon.jpg",
        author: "Caribbean Meal Prep Team",
        date: "June 2, 2026",
        read_time: "5 min read",
        category: "Haitian Heritage",
        keywords: [
            "diri djon djon",
            "Haitian black rice",
            "Haitian food delivery Massachusetts",
            "Caribbean meal prep Boston",
            "authentic Haitian meals",
        ],
        content: [
            {
                type: "p",
                text: "<em>Authentic Haitian black rice, hand-made weekly for busy professionals across Greater Boston.</em>",
            },
            {
                type: "p",
                text: "If you grew up in a Haitian household, the smell of <strong>diri djon djon</strong> cooking on the stove means something. It means a celebration. It means Sunday. It means somebody put real love and real time into the meal. And if you didn't grow up with it — once you taste it, you understand why Haitians treat this dish like treasure.",
            },
            {
                type: "p",
                text: 'At <strong>Caribbean Meal Prep</strong>, diri djon djon is one of our signature dishes, hand-made every week and delivered across Greater Boston. Here\'s the story behind it — and why it\'s the perfect meal for hard-working people who don\'t have hours to spend in the kitchen.',
            },
            { type: "h2", text: "What Is Diri Djon Djon?" },
            {
                type: "p",
                text: "Diri djon djon — often called <strong>Haitian black rice</strong> — is a beloved Haitian dish made by cooking rice in the dark, earthy broth of the djon djon mushroom. That mushroom is what gives the rice its striking black color and its deep, savory flavor. It's traditional white rice transformed into something rich, aromatic, and unmistakably Haitian.",
            },
            {
                type: "p",
                text: "This isn't everyday rice. In Haiti, diri djon djon is the rice you serve when company is coming.",
            },
            {
                type: "youtube",
                videoId: "wJXldsRdvmE",
                short: true,
                title: "Diri Djon Djon explained",
                caption:
                    "A short look at how diri djon djon is traditionally made (for educational purposes).",
            },
            {
                type: "h2",
                text: "The Djon Djon Mushroom: A Rare Treasure from Northern Haiti",
            },
            {
                type: "p",
                text: "What makes this dish so special starts with the mushroom itself. The <strong>djon djon</strong> is a tiny black mushroom that grows almost exclusively in the <strong>northern regions of Haiti</strong>. It's harvested, dried, and prized — both on the island and across the Haitian diaspora.",
            },
            {
                type: "p",
                text: "Because djon djon is rare and hard to find outside Haitian markets, authentic diri djon djon has always been a dish that takes real effort to make right. The dried mushrooms have to be soaked, the broth carefully strained, and the rice cooked low and slow so it drinks up every bit of that flavor.",
            },
            {
                type: "p",
                text: "That's exactly why so many busy families turn to meal prep for it — getting it right takes time most people simply don't have during the work week.",
            },
            { type: "h2", text: "What Does Diri Djon Djon Taste Like?" },
            {
                type: "p",
                text: "If you've never had it, picture this: an earthy, almost nutty depth, with a savory richness that coats every grain. It's comforting without being heavy. It pairs beautifully with Haitian staples like <strong>legim</strong>, <strong>griot</strong> (fried pork), <strong>poul nan sòs</strong> (chicken in sauce), or a simple side of pikliz.",
            },
            {
                type: "p",
                text: "It's the kind of dish that tastes like home — even if you've never been to Haiti.",
            },
            {
                type: "image",
                src: "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/mzt1j4xp_Screenshot_20260522_095333_ChatGPT.jpg",
                alt: "Caribbean Meal Prep — baked pork with Haitian diri djon djon black rice and mixed vegetables",
                caption:
                    "Our weekly Diri Djon Djon plate — paired with seasoned baked pork and fresh vegetables.",
            },
            {
                type: "h2",
                text: "Diri Djon Djon for Busy Professionals Across Greater Boston",
            },
            {
                type: "p",
                text: "Here's the honest truth: real diri djon djon takes time. Between sourcing the djon djon, soaking it, and cooking the rice properly, it's not a weeknight dish for someone working a full schedule.",
            },
            {
                type: "p",
                text: "That's where we come in. <strong>Caribbean Meal Prep</strong> makes authentic Haitian black rice fresh every week, so professionals across Greater Boston can eat the food they grew up loving — without giving up an evening to cook it.",
            },
            {
                type: "p",
                text: "We serve hard-working communities all over the region, including:",
            },
            {
                type: "ul",
                items: [
                    "<strong>Boston</strong> — Mattapan, Hyde Park, and Dorchester",
                    "<strong>Brockton, Randolph, and Milton</strong>",
                    "<strong>Cambridge, Somerville, and Everett</strong>",
                    "<strong>Quincy, Malden, Newton, and Waltham</strong>",
                ],
            },
            {
                type: "p",
                text: "Whether you're a nurse pulling long shifts, a professional commuting into the city, or a parent juggling work and family, we make it easy to come home to a real Haitian meal.",
            },
            {
                type: "h2",
                text: "Where to Get Authentic Haitian Black Rice in Massachusetts",
            },
            {
                type: "p",
                text: "If you've been searching for <strong>Haitian food delivery in Massachusetts</strong> or <strong>Caribbean meal prep near Boston</strong>, diri djon djon is one of the first dishes you should try. It's a taste of Haiti's heritage, made by people who respect the tradition behind it.",
            },
            {
                type: "p",
                text: "Hand-made weekly. Delivered fresh. No djon djon hunting, no hours at the stove — just authentic Haitian black rice, ready when you are.",
            },
            {
                type: "p",
                text: '<strong>Order this week\'s diri djon djon and taste Haiti without leaving home.</strong> <a href="/menu">Reserve your meal today →</a>',
            },
        ],
    },
    {
        slug: "healthy-caribbean-meal-prep-massachusetts",
        title:
            "Healthy Caribbean Meal Prep in Massachusetts: The Smarter Way to Eat, Save Time & Stay Fit",
        excerpt:
            "If you live in Massachusetts and find yourself too busy to cook healthy meals, you're not alone. Discover why busy professionals, gym-goers, and families across Greater Boston are turning to Caribbean meal prep.",
        hero_image:
            "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/pxk5bvtu_CaribbeanMealPrep%20blog1.png",
        author: "Caribbean Meal Prep Team",
        date: "May 26, 2026",
        read_time: "6 min read",
        category: "Boston Living",
        keywords: [
            "healthy meal prep Massachusetts",
            "Boston meal prep",
            "Caribbean meal prep",
            "high protein meal prep",
            "meal prep for busy professionals",
        ],
        content: [
            {
                type: "p",
                text: "If you live in Massachusetts and constantly find yourself too busy to cook healthy meals, you're not alone.",
            },
            {
                type: "p",
                text: "Between work, commuting, the gym, family responsibilities, and trying to maintain a healthy lifestyle, cooking every day can feel impossible. That's why more people across the Boston area are turning to <strong>healthy meal prep services</strong> to save time, eat better, and stay consistent with their goals.",
            },
            {
                type: "p",
                text: 'At <a href="https://www.caribbeanmealprep.net">Caribbean Meal Prep</a>, we believe healthy eating should never be boring. Our mission is simple: provide flavorful Caribbean-inspired meal prep designed for busy professionals, gym-goers, students, entrepreneurs, and families throughout Massachusetts.',
            },
            {
                type: "h2",
                text: "Why Meal Prep Is Growing Fast in Boston & Massachusetts",
            },
            {
                type: "p",
                text: "The demand for meal prep in the Boston area has exploded over the last few years. People want:",
            },
            {
                type: "ul",
                items: [
                    "Healthy food without cooking daily",
                    "Portion-controlled meals",
                    "Better nutrition",
                    "Time-saving solutions",
                    "Affordable alternatives to eating out",
                    "Convenient grab-and-go options",
                ],
            },
            {
                type: "p",
                text: "Whether you work long shifts, spend hours commuting, or simply don't enjoy cooking every night, meal prep helps eliminate stress while improving your eating habits.",
            },
            {
                type: "p",
                text: "Many professionals in Boston, Everett, Malden, Lawrence, Woburn, Quincy, Medford, and surrounding Massachusetts areas are now using meal prep services to stay on track with fitness and health goals.",
            },
            {
                type: "h2",
                text: "Healthy Eating Doesn't Mean Bland Food",
            },
            {
                type: "p",
                text: "One of the biggest problems with traditional healthy meal prep is flavor.",
            },
            {
                type: "p",
                text: "Too many meal prep companies serve plain chicken, dry rice, and repetitive meals that become boring after a week. That's where Caribbean Meal Prep is different.",
            },
            {
                type: "image",
                src: "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/2h4jxm8r_file_0000000003b071fda1d9461ad338c843.png",
                alt: "Black rice with grilled chicken and mixed vegetables — Caribbean Meal Prep",
                caption:
                    "Our Black Rice with Grilled Chicken & Mixed Vegetables — high protein, balanced, and packed with Caribbean flavor.",
            },
            {
                type: "p",
                text: "We combine:",
            },
            {
                type: "ul",
                items: [
                    "Authentic Caribbean flavor",
                    "Healthy ingredients",
                    "Balanced portions",
                    "High-protein meals",
                    "Fresh vegetables",
                    "Baked and grilled options",
                ],
            },
            {
                type: "p",
                text: "Our meals are designed to give you the comfort of Caribbean food while supporting healthy eating and active lifestyles.",
            },
            {
                type: "h2",
                text: "Meal Prep for Busy Professionals",
            },
            {
                type: "p",
                text: 'If you have "no time to cook," you are exactly who meal prep is built for.',
            },
            {
                type: "p",
                text: "Instead of:",
            },
            {
                type: "ul",
                items: [
                    "Spending hours grocery shopping",
                    "Cooking after work",
                    "Cleaning dishes every night",
                    "Ordering unhealthy fast food",
                ],
            },
            {
                type: "p",
                text: "You can have ready-to-eat meals prepared for your week.",
            },
            {
                type: "p",
                text: "Meal prep is especially helpful for:",
            },
            {
                type: "ul",
                items: [
                    "Nurses",
                    "Warehouse workers",
                    "Truck drivers",
                    "Entrepreneurs",
                    "Office professionals",
                    "Airport workers",
                    "College students",
                    "Parents with busy schedules",
                ],
            },
            {
                type: "p",
                text: "Healthy eating becomes easier when the hard work is already done for you.",
            },
            {
                type: "h2",
                text: "Meal Prep & Fitness Go Together",
            },
            {
                type: "p",
                text: "Fitness starts in the kitchen.",
            },
            {
                type: "p",
                text: "Whether your goal is:",
            },
            {
                type: "ul",
                items: [
                    "Weight loss",
                    "Muscle gain",
                    "Better energy",
                    "Portion control",
                    "Lean eating",
                    "High-protein nutrition",
                ],
            },
            {
                type: "p",
                text: "Meal prep helps create consistency.",
            },
            {
                type: "p",
                text: "Many people in Massachusetts struggle to stay on track because they:",
            },
            {
                type: "ul",
                items: [
                    "Skip meals",
                    "Eat late at night",
                    "Grab unhealthy fast food",
                    "Don't have time to cook after the gym",
                ],
            },
            {
                type: "p",
                text: "Prepared meals help remove those excuses.",
            },
            {
                type: "p",
                text: "At Caribbean Meal Prep, we focus on balanced meals with protein, vegetables, healthy carbs, and Caribbean-inspired flavor that supports active lifestyles.",
            },
            {
                type: "h2",
                text: "Why Caribbean Flavor Matters",
            },
            {
                type: "p",
                text: "Food is emotional. Food is culture.",
            },
            {
                type: "p",
                text: "Many people want healthier options without losing the flavors they grew up with. Caribbean Meal Prep brings together healthy eating and authentic island flavor in a way that feels satisfying, comforting, and sustainable.",
            },
            {
                type: "p",
                text: "Instead of forcing yourself to eat meals you don't enjoy, we create meals you actually look forward to eating.",
            },
            {
                type: "p",
                text: "That's what helps people stay consistent.",
            },
            {
                type: "h2",
                text: "Supporting Healthy Communities Across Massachusetts",
            },
            {
                type: "p",
                text: "Our goal is bigger than just food.",
            },
            {
                type: "p",
                text: "We want to help create healthier communities across Massachusetts by making healthy Caribbean meal prep more accessible, convenient, and enjoyable.",
            },
            {
                type: "p",
                text: "Whether you live near Boston, the North Shore, or surrounding Massachusetts areas, meal prep can help simplify your week while improving your lifestyle.",
            },
            {
                type: "h2",
                text: "Ready to Save Time & Eat Better?",
            },
            {
                type: "p",
                text: "Healthy eating should fit your lifestyle — not complicate it.",
            },
            {
                type: "p",
                text: "If you're looking for:",
            },
            {
                type: "ul",
                items: [
                    "Healthy meal prep in Massachusetts",
                    "Caribbean meal prep near Boston",
                    "High-protein meal prep",
                    "Healthy Caribbean food",
                    "Time-saving healthy meals",
                    "Meal prep for gym and fitness goals",
                    "Healthy meals for busy professionals",
                ],
            },
            {
                type: "p",
                text: 'Visit <a href="https://www.caribbeanmealprep.net">www.caribbeanmealprep.net</a> and discover flavorful meal prep designed for real life.',
            },
        ],
    },
];

// Helper: most recent first
export function getSortedPosts() {
    return [...BLOG_POSTS].sort((a, b) => (b.date < a.date ? -1 : 1));
}

export function getPostBySlug(slug) {
    return BLOG_POSTS.find((p) => p.slug === slug);
}
